import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Skeleton } from "../../feedback/Skeleton";

export type StepProgressCardSkeletonProps = HTMLAttributes<HTMLDivElement> & {
  steps?: number;
  surface?: "card" | "plain";
};

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim
 * from Card.tsx / the frozen baseline). */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

const StepProgressSkeletonRoot = styled("div", {
  label: "AgenticStepProgressCardSkeleton-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { surface: "card" | "plain" } }>(({ ownerState }) => [
  {
    width: "100%",
    maxWidth: "425px"
  },
  ownerState.surface === "card" && {
    ...fadedCardBorder,
    borderRadius: "var(--bfrs-radius-xl)",
    backgroundImage: "var(--bfrs-gradient-card)",
    padding: "1.25rem",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  }
]);

/* `&&` overrides beat Skeleton's own variant rules, reproducing the old
 * tailwind-merge resolution of the className overrides. */

const StepProgressSkeletonEyebrow = styled(Skeleton, {
  label: "AgenticStepProgressCardSkeleton-eyebrow"
})({
  "&&": {
    height: ".75rem",
    width: "9rem"
  }
});

const StepProgressSkeletonTitle = styled(Skeleton, {
  label: "AgenticStepProgressCardSkeleton-title"
})({
  "&&": {
    marginTop: ".5rem",
    height: "1.75rem",
    width: "18rem",
    maxWidth: "100%"
  }
});

const StepProgressSkeletonList = styled("div", {
  label: "AgenticStepProgressCardSkeleton-list"
})({
  marginTop: "21px",
  display: "grid",
  gap: "0px"
});

const StepProgressSkeletonRow = styled("div", {
  label: "AgenticStepProgressCardSkeleton-row"
})({
  display: "grid",
  minHeight: "35px",
  gridTemplateColumns: "16px minmax(0,1fr)",
  gap: ".5rem"
});

/* The original marker used cn("bfrs-mt-[3px] bfrs-size-2", active && "bfrs-mt-0 bfrs-size-4");
 * tailwind-merge dropped the earlier mt-/size- classes when active. */
const StepProgressSkeletonMarker = styled(Skeleton, {
  label: "AgenticStepProgressCardSkeleton-marker",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { active: boolean } }>(({ ownerState }) => ({
  "&&": ownerState.active
    ? { marginTop: 0, width: "1rem", height: "1rem" }
    : { marginTop: "3px", width: ".5rem", height: ".5rem" }
}));

const StepProgressSkeletonLabel = styled(Skeleton, {
  label: "AgenticStepProgressCardSkeleton-label"
})({
  "&&": {
    height: "1rem",
    width: "14rem"
  }
});

export const StepProgressCardSkeleton = forwardRef<HTMLDivElement, StepProgressCardSkeletonProps>(({ className, steps = 4, surface = "plain", ...props }, ref) => (
  <StepProgressSkeletonRoot ref={ref} ownerState={{ surface }} className={className} aria-hidden="true" {...props}>
    <StepProgressSkeletonEyebrow variant="text" />
    <StepProgressSkeletonTitle variant="text" />
    <StepProgressSkeletonList>
      {Array.from({ length: steps }, (_, index) => (
        <StepProgressSkeletonRow key={index}>
          <StepProgressSkeletonMarker variant="avatar" ownerState={{ active: index === 1 }} />
          <StepProgressSkeletonLabel variant="text" />
        </StepProgressSkeletonRow>
      ))}
    </StepProgressSkeletonList>
  </StepProgressSkeletonRoot>
));

StepProgressCardSkeleton.displayName = "StepProgressCardSkeleton";
